/**
 * Painel "Cenário do PCP" (só com `capacidade_fabrica:reprogramar`): escolhe
 * um pedido da fila e uma nova data de entrega, vê a fila reprojetada (quem
 * seria empurrado, a ocupação antes × depois) e recebe o que digitar na
 * planilha, linha por linha. A planilha continua sendo a fonte: nada é
 * gravado no backend. "Marcar para conferir" guarda neste aparelho o que foi
 * combinado, e a página confere na próxima leitura da planilha.
 */
import React, { useEffect, useMemo, useState } from "react";

import BotaoCopiar from "../../components/BotaoCopiar";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useToast } from "../../context/ToastContext";
import { MOTIVO_CENARIO, cenarioDeReprogramacao, ehIsoValido, novaMarca } from "../../utils/capacidade";
import { plural } from "../../utils/dinheiro";
import { ddmm } from "./apresentacao";
import {
  pedidosDaFila,
  podeMarcar,
  rotuloDoPedido,
  textoIndisponivel,
  textoParaCopiar,
} from "./apresentacaoSimulacao";
import { ListaEmpurrados, OcupacaoAntesDepois } from "./ResultadoComparacao";
import { Aviso, Regua, Selo } from "./styles";
import { BotaoAcao, CorpoPainel, Formulario, Instrucao, LinhaBotoes, ListaInstrucoes, Secao } from "./stylesSimulacao";

// Com o artigo junto ("da posição", "das posições"): a frase muda de número
// conforme o pedido tem um ou vários ambientes.
function textoPosicoes(primeira, ultima, { de = false } = {}) {
  if (primeira === null) return "—";
  if (primeira === ultima) return `${de ? "da" : "a"} posição ${primeira}`;
  return `${de ? "das" : "as"} posições ${primeira} a ${ultima}`;
}

function posicoesAntes(itens) {
  const lista = itens.map((i) => i.antes.posicao).filter(Boolean);
  return lista.length ? { primeira: Math.min(...lista), ultima: Math.max(...lista) } : { primeira: null, ultima: null };
}

function PedidoNaFila({ cenario, novaData }) {
  const { pedidoDepois, itens } = cenario;
  const antes = posicoesAntes(itens);
  return (
    <Secao aria-label="O pedido na fila">
      <h3>O pedido na fila</h3>
      <p>
        Sai {textoPosicoes(antes.primeira, antes.ultima, { de: true })} para{" "}
        <strong>{textoPosicoes(pedidoDepois.primeira, pedidoDepois.ultima)}</strong>. A marcenaria termina por volta de{" "}
        {ddmm(pedidoDepois.termino)}; para entregar em {ddmm(novaData)} precisa terminar até {ddmm(pedidoDepois.limite)}.
        {pedidoDepois.podeAtrasar ? <Selo $tom="warning">pode atrasar</Selo> : null}
      </p>
    </Secao>
  );
}

function OQueDigitar({ itens, onMarcar }) {
  const marcaveis = itens.filter((item) => podeMarcar(item.instrucao));
  return (
    <Secao aria-label="O que digitar na planilha">
      <h3>O que digitar na planilha</h3>
      <ListaInstrucoes>
        {itens.map((item) => (
          <Instrucao key={item.identidade || item.chave}>
            <span className="onde" title={`Linha ${item.linha_planilha} · ${item.ambiente}`}>
              Linha {item.linha_planilha} · {item.ambiente}
            </span>
            <span className="texto">{item.instrucao.texto}</span>
            {item.instrucao.avisos.map((aviso) => (
              <span key={aviso} className="aviso">
                Atenção: {aviso}.
              </span>
            ))}
          </Instrucao>
        ))}
      </ListaInstrucoes>
      <LinhaBotoes>
        <BotaoCopiar texto={textoParaCopiar(itens)} rotulo="Copiar instruções" rotuloCopiado="Copiado" />
        {marcaveis.length > 0 ? (
          <BotaoAcao type="button" onClick={() => onMarcar(marcaveis)}>
            Marcar para conferir
          </BotaoAcao>
        ) : null}
      </LinhaBotoes>
      <p>
        Os dias úteis daqui seguem a aba TABELAS da planilha (o calendário que o Excel usa nas fórmulas); a fila usa o
        calendário da unidade. &quot;Marcar para conferir&quot; guarda neste aparelho e confere na próxima leitura da
        planilha.
      </p>
    </Secao>
  );
}

function Resultado({ cenario, novaData, onMarcar }) {
  if (cenario.indisponivel) {
    const texto =
      cenario.motivo === MOTIVO_CENARIO.PEDIDO_SEM_ABERTOS
        ? "Este pedido não tem ambiente aberto na fila (em produção, aprovado ou aguardando aprovação)."
        : textoIndisponivel(cenario);
    return (
      <Aviso $tom="warning" role="note">
        {texto}
      </Aviso>
    );
  }
  return (
    <>
      {cenario.calendarioAusente.length > 0 ? (
        <Aviso $tom="warning" role="note">
          Falta cadastrar os feriados de {cenario.calendarioAusente.join(", ")} em Configurações &gt; Calendário: os dias
          úteis da fila podem estar errados.
        </Aviso>
      ) : null}
      <PedidoNaFila cenario={cenario} novaData={novaData} />
      <OcupacaoAntesDepois ocupacao={cenario.ocupacao} />
      <ListaEmpurrados empurrados={cenario.empurrados} />
      <OQueDigitar itens={cenario.itens} onMarcar={onMarcar} />
    </>
  );
}

/**
 * `pedidoInicial` = `"pedido|cliente"` quando aberto a partir de uma lista;
 * `onMarcar(marcas)` guarda as marcas (useMarcasReprogramacao).
 */
export default function CenarioPcp({
  aberto,
  onFechar,
  pedidoInicial,
  painel,
  hoje,
  G,
  feriadosPlanilha,
  tabelasAusente,
  anosComRegistro,
  escala,
  onMarcar,
}) {
  const toast = useToast();
  const pedidos = useMemo(() => pedidosDaFila(painel, hoje), [painel, hoje]);
  const [chave, setChave] = useState(pedidoInicial || "");
  const [novaData, setNovaData] = useState("");

  useEffect(() => {
    if (!aberto) return;
    setChave(pedidoInicial || "");
    setNovaData("");
  }, [aberto, pedidoInicial]);

  const escolhido = pedidos.find((p) => p.chave === chave) || null;
  const dataValida = ehIsoValido(novaData) && novaData >= hoje;
  const cenario = useMemo(() => {
    if (!escolhido || !dataValida || !painel?.ok) return null;
    return cenarioDeReprogramacao({
      painel,
      pedido: escolhido.pedido,
      cliente: escolhido.cliente,
      novaData,
      G,
      feriadosPlanilha,
      anosComRegistro,
      escala,
    });
  }, [escolhido, dataValida, painel, novaData, G, feriadosPlanilha, anosComRegistro, escala]);

  const marcar = (itens) => {
    onMarcar(itens.map((item) => novaMarca({ item, instrucao: item.instrucao, hoje })));
    toast?.success?.(
      `${plural(itens.length, "ambiente marcado", "ambientes marcados")}: na próxima leitura da planilha a página confere.`,
    );
  };

  const atrasados = pedidos.filter((p) => p.atrasado);
  const demais = pedidos.filter((p) => !p.atrasado);
  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Cenário do PCP"
      subtitle="Mover um pedido para outra data e ver o efeito na fila. Nada é gravado."
      width="min(94vw, 600px)"
      ariaLabelledBy="cenario-pcp-titulo"
    >
      <CorpoPainel>
        <Secao aria-label="O que mudar">
          <Formulario>
            <label>
              Pedido
              <select value={chave} onChange={(e) => setChave(e.target.value)}>
                <option value="">Escolha um pedido da fila</option>
                {atrasados.length > 0 ? (
                  <optgroup label="Atrasados">
                    {atrasados.map((p) => (
                      <option key={p.chave} value={p.chave}>
                        {rotuloDoPedido(p)}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
                <optgroup label="Na fila">
                  {demais.map((p) => (
                    <option key={p.chave} value={p.chave}>
                      {rotuloDoPedido(p)}
                    </option>
                  ))}
                </optgroup>
              </select>
            </label>
            <label>
              Nova entrega
              <input type="date" min={hoje} value={novaData} onChange={(e) => setNovaData(e.target.value)} />
            </label>
          </Formulario>
          {novaData && !dataValida ? <p role="alert">A nova entrega precisa ser de hoje em diante.</p> : null}
        </Secao>
        {tabelasAusente ? (
          <Aviso $tom="warning" role="note">
            A planilha não tem a aba TABELAS: o &quot;o que digitar&quot; conta só sábado e domingo como dia sem
            expediente, e o Excel pode calcular outra data.
          </Aviso>
        ) : null}
        {cenario ? (
          <Resultado cenario={cenario} novaData={novaData} onMarcar={marcar} />
        ) : (
          <Regua>Escolha o pedido e a nova data de entrega.</Regua>
        )}
      </CorpoPainel>
    </SlideOverPanel>
  );
}
